<!--
 * @Description: 
 * @Date: 2024-03-15 13:09:22
-->
<template>
  <BaseForm
    ref="FormRef"
    :model="formData"
    label-suffix="："
    label-position="right"
    :disabled="disabled"
  >
    <BaseFormItem
      v-for="item in columns"
      :key="item.prop"
      :prop="item.prop"
      :label="item.label"
      :rules="rules[item.prop]"
    >
      <template v-if="item.type === 'input'">
        <BaseInput
          style="width: 100%"
          :disabled="disableKeys.has(item.prop)"
          v-model="formData[item.prop]"
          :placeholder="`请输入${item.label}`"
          v-bind="item.config"
        ></BaseInput>
      </template>
      <template v-if="item.type === 'select'">
        <BaseSelect
          style="width: 100%"
          :disabled="disableKeys.has(item.prop)"
          v-model="formData[item.prop]"
          :placeholder="`请选择${item.label}`"
          v-bind="item.config"
        ></BaseSelect>
      </template>
      <template v-if="item.type === 'img'">
        <template v-if="disabled && !formData[item.prop]">
          <span>-</span>
        </template>
        <BaseUpload
          v-else
          style="width: 100%"
          :file-list="
            formData[item.prop]
              ? [
                  {
                    url: formData[item.prop],
                    name: '药品图片',
                    status: 'success',
                    uid: 1
                  }
                ]
              : []
          "
          class="custom-upload"
          :class="{ 'has-img': formData[item.prop] }"
          auto-upload
          drag
          list-type="picture-card"
          :http-request="
            async ({ file }) => {
              Reflect.set(formData, item.prop, await uploadFile(file));
              Reflect.set(formData, `file_${item.prop}`, file);
            }
          "
          :before-remove="
            () => {
              Reflect.set(formData, item.prop, '');
              return true;
            }
          "
          :disabled="disableKeys.has(item.prop) || disabled"
          :accept="['image/*']"
          @error="
            ({ msg }) => {
              XrkMessage.error(msg);
            }
          "
          :before-selected="
            (() => {
              let show = false;
              return file => {
                if (!file.type) {
                  !show &&
                    (show = true) &&
                    XrkMessage.error({
                      message: '不支持上传此类文件',
                      onClose: () => (show = false)
                    });
                  throw '不支持上传此类文件';
                }
                return false;
              };
            })()
          "
        ></BaseUpload>
      </template>
      <template v-if="item.type === 'search'">
        <div style="display: flex; gap: 12px; width: 100%">
          <BaseInput
            clearable
            style="flex-grow: 1"
            :disabled="disableKeys.has(item.prop)"
            v-model="formData[item.prop]"
            :placeholder="`请输入${item.label}`"
            v-bind="item.config"
          ></BaseInput>
          <BaseButton
            :disabled="!formData[item.prop] || disableKeys.has(item.prop)"
            :click="async () => await item.searchFn?.(formData[item.prop])"
          >
            查询
          </BaseButton>
        </div>
      </template>
    </BaseFormItem>
  </BaseForm>
</template>

<script setup lang="tsx">
import {
  BaseButton,
  BaseForm,
  BaseFormItem,
  BaseFormRef,
  BaseInput,
  BaseInputProps,
  BaseSelect,
  BaseSelectProps,
  BaseUpload,
  XrkMessage
} from 'xrk-components';
import { defineProps, ref, defineExpose } from 'vue';
import { Drug } from '../../data-source/types';
import { getProductOtherInfo } from '../../data-source/api';
import {
  PRODUCT_DRUG,
  PRODUCT_SOURCE_LIBRARY,
  PRODUCT_SOURCE_PROJECT
} from '../../config';
type EditRow = Partial<Drug['row']>;
const props = defineProps<{
  isAdd?: boolean;
  disabled?: boolean;
  row: EditRow;
}>();

const FormRef = ref<BaseFormRef>();

const Reflect = window.Reflect;
const uploadFile = async (file: File) => {
  return URL.createObjectURL(file);
};

const formData = ref<{ [key in keyof typeof props.row]: any }>({
  ...props.row
});
const needDealKeys: Array<keyof EditRow> = [
  'name',
  'productName',
  'medicineType',
  'jixing',
  'scope'
];

const disableKeys = ref<Set<keyof EditRow>>(new Set());
if (
  [PRODUCT_SOURCE_LIBRARY, PRODUCT_SOURCE_PROJECT].includes(props.row.source) ||
  props.isAdd
) {
  disableKeys.value = new Set(needDealKeys);
  if (!props.isAdd) {
    disableKeys.value.add('approvalNumber');
  }
}
if (props.row.isOtcFixed == 1) {
  // 处方药后不能修改
  disableKeys.value.add('isOtc');
}

const _baseInputConfig = { maxlength: 20, clearable: true } as BaseInputProps;

const columns: Array<{
  type?: 'input' | 'img' | 'search' | 'select';
  prop: any;
  label: string;
  required?: boolean;
  config?: any;
  disable?: boolean;
  searchFn?: (searchVal: string | number | undefined) => Promise<void> | void;
}> = [
  {
    prop: 'isOtc',
    label: '是否处方药',
    type: 'select',
    required: true,
    config: {
      options: [
        { label: '是', value: 1 },
        { label: '否', value: 0 }
      ],
      clearable: true
    } as BaseSelectProps
  },
  {
    prop: 'approvalNumber',
    label: '批准文号',
    type: 'search',
    required: true,
    config: _baseInputConfig,
    searchFn: async searchVal => {
      const { data } = await getProductOtherInfo({
        approvalNumber: searchVal,
        type: PRODUCT_DRUG
      });
      const isEmpty = !data || Object.keys(data).length === 0;
      if (isEmpty) XrkMessage.error('暂未查询到信息');
      needDealKeys.forEach(key => {
        if (isEmpty) {
          disableKeys.value.delete(key);
          formData.value[key] = '';
        } else {
          disableKeys.value.add(key);
          formData.value[key] = data?.[key] || '-';
        }
      });
      setTimeout(() => FormRef.value.validateField(needDealKeys), 0);
    }
  },
  {
    prop: 'name',
    label: '药品名称',
    type: 'input',
    required: true,
    config: _baseInputConfig
  },
  {
    prop: 'productName',
    label: '商品名称',
    type: 'input',
    required: true,
    config: _baseInputConfig
  },
  {
    prop: 'medicineType',
    label: '药品类型',
    type: 'input',
    required: true,
    config: _baseInputConfig
  },
  {
    prop: 'jixing',
    label: '剂型',
    type: 'input',
    required: true,
    config: _baseInputConfig
  }
];

const rules = columns.reduce((prev, cur) => {
  prev[cur.prop] = cur.required
    ? [
        {
          required: true,
          message: `请${
            ['input', 'search'].includes(cur.type) ? '输入' : '选择'
          }${cur.label}`,
          trigger: 'blur'
        }
      ]
    : [];
  return prev;
}, {});

defineExpose({
  getFormData: async () => {
    await FormRef.value.validate();
    const res = { ...formData.value };
    Array.from(disableKeys.value).forEach(key => {
      if (res[key] === '-') {
        res[key] = '';
      }
    });
    return res;
  }
});
</script>

<style lang="scss" scoped>
.custom-upload {
  ::v-deep {
    .xrk-upload {
      --xrk-upload-dragger-padding-horizontal: 20px;
    }
  }
}
.has-img {
  ::v-deep {
    .xrk-upload__list {
      margin-top: 0;
    }
    .xrk-upload.is-drag,
    .xrk-upload__tip {
      display: none;
    }
    .xrk-upload__item_img {
      width: 100%;
      margin: 0;
    }
  }
}
</style>
